import * as React from "react";
import {
  useRegisterPushSubscription,
  useUnregisterPushSubscription,
} from "@njoy/controller";
import Button from "../components/uiMerge/newButton"

import { syncBrowserPushSubscription } from "./pushNotifications";
import { isPushOptedOut } from "./pushNotifications";
import {
  hasShownFirstLoginPushPrompt,
  markFirstLoginPushPromptShown,
  pushPromptNeededEventName,
} from "./pushNotifications";
import * as ROUTES from "../constants/routes";

export const PushNotificationBootstrap: React.FC = () => {
  const { registerPushSubscription } = useRegisterPushSubscription();
  const { unregisterPushSubscription } = useUnregisterPushSubscription();
  const hasSyncedRef = React.useRef(false);
  const [showPrompt, setShowPrompt] = React.useState(false);

  const canPromptForPush = React.useCallback((): boolean => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      return false;
    }

    return Notification.permission !== "granted" && !isPushOptedOut();
  }, []);

  React.useEffect(() => {
    if (hasSyncedRef.current) {
      return;
    }

    hasSyncedRef.current = true;
    let active = true;

    const run = async () => {
      if (typeof window === "undefined" || !("Notification" in window)) {
        return;
      }

      if (Notification.permission !== "granted") {
        return;
      }

      if (isPushOptedOut()) {
        return;
      }

      try {
        await syncBrowserPushSubscription({
          registerPushSubscription,
          unregisterPushSubscription,
        });
      } catch {
        if (!active) {
          return;
        }
      }
    };

    void run();

    return () => {
      active = false;
    };
  }, [registerPushSubscription, unregisterPushSubscription]);

  React.useEffect(() => {
    if (!canPromptForPush()) {
      return;
    }

    if (!hasShownFirstLoginPushPrompt()) {
      markFirstLoginPushPromptShown();
      setShowPrompt(true);
    }
  }, [canPromptForPush]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const onPromptNeeded = () => {
      if (canPromptForPush()) {
        setShowPrompt(true);
      }
    };

    window.addEventListener(pushPromptNeededEventName, onPromptNeeded);

    return () => {
      window.removeEventListener(pushPromptNeededEventName, onPromptNeeded);
    };
  }, [canPromptForPush]);

  if (!showPrompt) {
    return null;
  }


  return (
    <div style={{ background: `linear-gradient(180deg, rgba(77, 68, 85, 1) 17.5%, rgba(56, 70, 106, 1) 99.8%)` }} className="fixed top-4 left-1/2 z-[1200] w-[92%] max-w-[420px] -translate-x-1/2 rounded-xl p-4 shadow-[0_12px_30px_rgba(0,0,0,0.35)]">
      <p className="text-sm font-semibold text-white">Turn on push notifications</p>
      <p className="mt-1 text-xs text-[#bdbdbd]">
        Enable push in settings so you never miss alerts, offer updates, and transaction status.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <Button
          variant="secondary"
          type="button"
          className="!px-3 !py-2 !text-xs h-[34px] !font-semibold !text-white"
          onClick={() => setShowPrompt(false)}
        >
          Later
        </Button>
        <Button
          variant="primary"
          type="button"
          className=" !bg-primary !px-3 !py-2 !text-xs !font-semibold !text-white h-[34px]"
          onClick={() => {
            setShowPrompt(false);
            window.location.assign(ROUTES.APP_SETTINGS);
          }}
        >
          Open settings
        </Button>
      </div>
    </div>
  );
};